// Copyright (c) Ping. All rights reserved.
//
// Thin wire-format adapter for the Settings → Account pane.
//
// Display name + discoverability ride the canonical
//   PATCH /v1/me/profile
// (openapi.yaml `ProfilePatchRequest`). The same endpoint backs the
// signup flow's "What should we call you?" step (see
// `useAuthFlowCoordinator.finalizeRegister`), so both code paths
// converge on a single contract.
//
// The avatar presign flow still rides the legacy
//   POST /v1/profiles/avatar/upload-url + PUT /v1/profiles { avatar_blob_key }
// pair until the canonical `avatar_media_id` upload pipeline lands.
// See `legacyUpdateProfileAvatar` below; this is the only remaining
// caller of the retired `PUT /v1/profiles`.

import { patch, post } from '@core/network/api';

/**
 * Canonical profile patch — matches `ProfilePatchRequest` in
 * openapi.yaml. Any field left `undefined` is omitted from the wire
 * body so the BE treats it as "no change".
 */
export interface UpdateProfileBody {
  readonly displayName?: string;
  readonly discoverable?: boolean;
  /**
   * Pre-existing media id from the avatar-upload pipeline. NOT the
   * legacy presign `blob_key` — use `legacyUpdateProfileAvatar` for
   * that until the new pipeline ships.
   */
  readonly avatarMediaId?: string | null;
  // Display profile fields (PATCH semantics: '' clears, undefined keeps).
  readonly bio?: string;
  readonly jobTitle?: string;
  readonly companyName?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly timezone?: string;
  // Ephemeral status — propagated to peers.
  readonly statusEmoji?: string;
  readonly statusText?: string;
  /** Epoch ms; 0 clears the expiry. */
  readonly statusExpiresAt?: number;
}

interface ProfilePatchWireBody {
  display_name?: string;
  discoverable?: boolean;
  avatar_media_id?: string | null;
  bio?: string;
  job_title?: string;
  company_name?: string;
  contact_email?: string;
  contact_phone?: string;
  timezone?: string;
  status_emoji?: string;
  status_text?: string;
  status_expires_at?: number;
}

interface ProfileStatusWire {
  emoji?: string | null;
  text: string;
  expires_at?: number | null;
}

interface ProfileResponseWire {
  account_id: string;
  display_name: string;
  discoverable: boolean;
  avatar_media_id?: string | null;
  bio?: string | null;
  job_title?: string | null;
  company_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  timezone?: string | null;
  status?: ProfileStatusWire | null;
  verified?: boolean;
}

export interface ProfileStatus {
  readonly emoji: string | null;
  readonly text: string;
  readonly expiresAt: number | null;
}

export interface ProfileResponse {
  readonly accountId: string;
  readonly displayName: string;
  readonly discoverable: boolean;
  readonly avatarMediaId: string | null;
  readonly bio: string | null;
  readonly jobTitle: string | null;
  readonly companyName: string | null;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly timezone: string | null;
  readonly status: ProfileStatus | null;
  readonly verified: boolean;
}

function toProfileResponse(w: ProfileResponseWire): ProfileResponse {
  return {
    accountId: w.account_id,
    displayName: w.display_name,
    discoverable: w.discoverable,
    avatarMediaId: w.avatar_media_id ?? null,
    bio: w.bio ?? null,
    jobTitle: w.job_title ?? null,
    companyName: w.company_name ?? null,
    contactEmail: w.contact_email ?? null,
    contactPhone: w.contact_phone ?? null,
    timezone: w.timezone ?? null,
    status: w.status
      ? { emoji: w.status.emoji ?? null, text: w.status.text, expiresAt: w.status.expires_at ?? null }
      : null,
    verified: w.verified === true,
  };
}

export async function updateProfile(body: UpdateProfileBody): Promise<ProfileResponse> {
  const wire: ProfilePatchWireBody = {};
  if (body.displayName !== undefined) wire.display_name = body.displayName;
  if (body.discoverable !== undefined) wire.discoverable = body.discoverable;
  if (body.avatarMediaId !== undefined) wire.avatar_media_id = body.avatarMediaId;
  if (body.bio !== undefined) wire.bio = body.bio;
  if (body.jobTitle !== undefined) wire.job_title = body.jobTitle;
  if (body.companyName !== undefined) wire.company_name = body.companyName;
  if (body.contactEmail !== undefined) wire.contact_email = body.contactEmail;
  if (body.contactPhone !== undefined) wire.contact_phone = body.contactPhone;
  if (body.timezone !== undefined) wire.timezone = body.timezone;
  if (body.statusEmoji !== undefined) wire.status_emoji = body.statusEmoji;
  if (body.statusText !== undefined) wire.status_text = body.statusText;
  if (body.statusExpiresAt !== undefined) wire.status_expires_at = body.statusExpiresAt;

  const response = await patch<ProfileResponseWire>('/v1/me/profile', wire);
  return toProfileResponse(response);
}

/**
 * Fetches the authenticated user's profile via GET /v1/me/profile.
 * Seeds the Settings UI (display name, contact, bio, avatar, discoverable,
 * verified) with the canonical server-side values.
 */
export async function getProfile(): Promise<ProfileResponse> {
  const { get } = await import('@core/network/api');
  const response = await get<ProfileResponseWire>('/v1/me/profile');
  return toProfileResponse(response);
}

// ── Verified-contact OTP flow (Account → Identity verification) ──────────

interface VerifyContactStartWire {
  challenge_id: string;
  expires_at: number;
}

export interface VerifyContactStart {
  readonly challengeId: string;
  readonly expiresAt: number;
}

export async function verifyContactStart(): Promise<VerifyContactStart> {
  const w = await post<VerifyContactStartWire>('/v1/me/verify-contact/start', {});
  return { challengeId: w.challenge_id, expiresAt: w.expires_at };
}

export async function verifyContactConfirm(challengeId: string, code: string): Promise<boolean> {
  const w = await post<{ verified: boolean }>('/v1/me/verify-contact/confirm', {
    challenge_id: challengeId,
    code,
  });
  return w.verified === true;
}

/**
 * Bind a freshly-uploaded avatar blob to the profile. The old
 * `PUT /v1/profiles { avatar_blob_key }` endpoint never existed on the API
 * (404); the canonical bind is `PATCH /v1/me/profile { avatar_media_id }`
 * (`profile.PatchMe` decodes it as the base64url media id). The presigned
 * upload returns the blob key, which IS the media id.
 */
export async function legacyUpdateProfileAvatar(avatarBlobKey: string): Promise<void> {
  await patch('/v1/me/profile', { avatar_media_id: avatarBlobKey });
}

export type AvatarContentType = 'image/jpeg' | 'image/png' | 'image/webp';

interface AvatarUploadUrlWireResponse {
  put_url: string;
  blob_key: string;
  headers: Record<string, string>;
  expires_at: string;
}

export interface AvatarUploadUrl {
  readonly putUrl: string;
  readonly blobKey: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly expiresAt: string;
}

export async function requestAvatarUploadUrl(
  contentType: AvatarContentType,
): Promise<AvatarUploadUrl> {
  const wire = await post<AvatarUploadUrlWireResponse>('/v1/profiles/avatar/upload-url', {
    content_type: contentType,
  });
  return {
    putUrl: wire.put_url,
    blobKey: wire.blob_key,
    headers: wire.headers,
    expiresAt: wire.expires_at,
  };
}

/**
 * Uploads `file` directly to the S3-presigned PUT URL returned by
 * `requestAvatarUploadUrl`. The API process never sees the bytes —
 * the client streams them straight to object storage. Throws on
 * non-2xx S3 responses.
 */
export async function uploadAvatarBytes(upload: AvatarUploadUrl, file: Blob): Promise<void> {
  const response = await fetch(upload.putUrl, {
    method: 'PUT',
    headers: upload.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`);
  }
}
